import { DropdownMenu } from "@/components/shared/dropdown-menu";
import {
  CreateSampleFormParams,
  QaRules,
} from "@/v2/entities/sampling/Sampling.api";
import { Button, Em, Flex, Text } from "@radix-ui/themes";
import { RuleBuilder } from "./RuleBuilder";
import styles from "./SampleForm.module.css";
import { Step } from "./Step";

interface TargetRuleBuilderProps {
  value?: QaRules;
  onChange: (value?: QaRules) => void;
  viewOnly?: boolean;
  size?: "1" | "2" | "3";
}

export function TargetRuleBuilder(props: TargetRuleBuilderProps) {
  if (props.viewOnly && (props.value == null || Object.keys(props.value).length === 0)) {
    return (
      <div
        data-accent-color="gray"
        className={`rt-reset rt-BaseButton rt-r-size-${props.size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
      >
        All interactions
      </div>
    );
  }

  return (
    <RuleBuilder
      value={props.value ?? {}}
      onChange={props.onChange}
      viewOnly={props.viewOnly}
      allowedFields={["channel", "team"]}
      size={props.size}
    />
  );
}

interface TargetStepProps {
  value: CreateSampleFormParams;
  onChange: (value: CreateSampleFormParams) => void;
  error?: string;
}

export function TargetStep({ value, onChange, error }: TargetStepProps) {
  return (
    <Step index={1} label="Target" description="Pick interactions to score">
      <Flex direction={"column"} gap={"2"} className={styles.targetStep}>
        <Text size={"3"} color={"gray"}>
          This scorecard will be used for
          <DropdownMenu
            items={[
              {
                label: "All Interactions",
                onClick: () => onChange({ ...value, qaRules: null }),
              },
              {
                label: "Custom Interactions",
                onClick: () =>
                  onChange({
                    ...value,
                    qaRules: {
                      and: [],
                    },
                  }),
              },
            ]}
          >
            <DropdownMenu.Trigger>
              <Button
                variant="soft"
                color="gray"
                highContrast
                style={{
                  marginTop: -4,
                  marginLeft: 4,
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {value.qaRules === null
                  ? "All Interactions"
                  : "Custom Interactions"}
              </Button>
            </DropdownMenu.Trigger>
          </DropdownMenu>
        </Text>

        {value.qaRules !== null ? (
          <TargetRuleBuilder
            value={value.qaRules}
            onChange={(newQaRules) => {
              onChange({ ...value, qaRules: newQaRules });
            }}
          />
        ) : (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--blue-2)",
              border: "1px solid var(--blue-4)",
              borderRadius: 6,
              color: "var(--blue-11)",
              fontSize: 14,
              lineHeight: 1.5,
              display: "inline-block",
              maxWidth: "fit-content",
            }}
          >
            👉 Use <b>Custom Interactions</b> to assign specific Scorecards to
            different teams or channels.
          </div>
        )}

        {error && (
          <Text size={"1"} color={"red"}>
            <Em>{error}</Em>
          </Text>
        )}
      </Flex>
    </Step>
  );
}
